import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Card, Button, Empty, KPI, Input, Select, Label } from "@/components/Primitives";
import { StatusPill, invoiceStatusVariant, ticketStatusVariant } from "@/components/StatusPill";
import { ChevronLeft, Link2, Unlink, Plus, AlertTriangle, Download, X, Ticket } from "lucide-react";
import { toast } from "sonner";

function gbp(v) {
  const n = v ?? 0;
  const hasPence = Math.round(n * 100) !== Math.round(n) * 100;
  return `£${n.toLocaleString(undefined, { minimumFractionDigits: hasPence ? 2 : 0, maximumFractionDigits: 2 })}`;
}

function statusVariant(s) {
  if (s === "completed") return "success";
  if (s === "in_progress") return "primary";
  if (s === "review") return "warning";
  return "default";
}

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [showAttach, setShowAttach] = useState(false);
  const [tickets, setTickets] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setData(data);
    } catch {
      toast.error("Project not found");
      nav("/projects");
    }
  };
  const loadTickets = async () => {
    try {
      const { data } = await api.get(`/tickets`, { params: { project_id: id } });
      setTickets(data || []);
    } catch { /* non-blocking */ }
  };
  useEffect(() => { load(); loadTickets(); /* eslint-disable-next-line */ }, [id]);

  const detach = async (iid) => {
    await api.post(`/projects/${id}/detach-invoice/${iid}`);
    toast.success("Invoice detached");
    load();
  };

  if (!data) return <div className="text-sm text-muted">Loading…</div>;
  const { project: p, financials: f, invoices, work_orders, assignees } = data;
  const isFixed = f.billing_type === "fixed";
  const overBudget = isFixed && f.budget_used_pct > 100;

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb={`${p.customer_name} · Projects`}
        title={
          <div className="flex items-center gap-3">
            <button onClick={() => nav("/projects")} className="p-1 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))]"><ChevronLeft className="w-5 h-5" strokeWidth={2}/></button>
            {p.name}
          </div>
        }
        subtitle={<span className="inline-flex items-center gap-2"><StatusPill variant={statusVariant(p.status)}>{(p.status || "").replace("_", " ")}</StatusPill><span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${isFixed ? "bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))]" : "bg-amber-500/15 text-amber-600 dark:text-amber-400"}`}>{isFixed ? "Fixed price" : "Ongoing"}</span></span>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {isFixed ? (
          <>
            <KPI label="Agreed budget" value={gbp(f.budget)} variant="primary" />
            <KPI label="Paid" value={gbp(f.paid)} variant="success" sub={`${f.budget_used_pct}% of budget`}/>
            <KPI label="Outstanding" value={gbp(f.outstanding)} variant={f.outstanding > 0 ? "warning" : "default"}/>
            <KPI label="Remaining" value={gbp(f.remaining)} variant={overBudget ? "danger" : "default"}/>
          </>
        ) : (
          <>
            <KPI label="Invoiced" value={gbp(f.invoiced)} variant="primary" sub="All-time"/>
            <KPI label="Paid" value={gbp(f.paid)} variant="success"/>
            <KPI label="Outstanding" value={gbp(f.outstanding)} variant={f.outstanding > 0 ? "warning" : "default"}/>
            <KPI label="Billing" value="Ongoing" sub="No cap"/>
          </>
        )}
      </div>

      {isFixed && f.budget > 0 && (
        <Card title="Budget usage">
          <div className="p-5">
            <div className="flex items-baseline justify-between mb-2">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-mono font-bold">{gbp(f.paid)}</span>
                <span className="text-[13px] text-[hsl(var(--text-muted))]">paid · {gbp(f.outstanding)} outstanding</span>
              </div>
              <span className={`text-[12px] font-semibold ${overBudget ? "text-rose-600" : "text-[hsl(var(--text-muted))]"}`}>
                {overBudget && <AlertTriangle className="w-3 h-3 inline mr-1" strokeWidth={2}/>}
                {f.budget_used_pct}% of {gbp(f.budget)}
              </span>
            </div>
            <div className="h-3 rounded-full bg-[hsl(var(--surface-hover))] overflow-hidden relative">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${Math.min(f.budget_used_pct, 100)}%`,
                  background: overBudget ? "#F43F5E" : "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--primary-hover)))",
                }}
              />
              {(f.outstanding > 0 && !overBudget) && (
                <div
                  className="absolute top-0 h-full bg-amber-500/40"
                  style={{
                    left: `${Math.min(f.budget_used_pct, 100)}%`,
                    width: `${Math.min((f.outstanding / f.budget) * 100, 100 - f.budget_used_pct)}%`,
                  }}
                />
              )}
            </div>
            {overBudget && (
              <div className="mt-3 text-[12px] text-rose-600 dark:text-rose-400 inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" strokeWidth={2}/>Over budget by {gbp(f.paid - f.budget)}
              </div>
            )}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card
          className="lg:col-span-2"
          title="Allocated invoices"
          right={
            <Button size="sm" variant="secondary" onClick={() => setShowAttach(true)} data-testid="attach-invoice-btn">
              <Link2 className="w-3.5 h-3.5" strokeWidth={2}/>Attach invoice
            </Button>
          }
        >
          {invoices.length === 0 ? (
            <Empty title="No invoices attached" subtitle="Use 'Attach invoice' to allocate one to this project." />
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))]">
                  <th className="text-left font-semibold px-5 py-3">Number</th>
                  <th className="text-left font-semibold px-5 py-3">Status</th>
                  <th className="text-left font-semibold px-5 py-3">Issued</th>
                  <th className="text-right font-semibold px-5 py-3">Amount</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {invoices.map((i) => (
                  <tr key={i.id} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-hover))] group" data-testid="project-invoice-row">
                    <td className="px-5 py-3 font-mono font-semibold">{i.number}</td>
                    <td className="px-5 py-3"><StatusPill variant={invoiceStatusVariant(i.status)}>{i.status}</StatusPill></td>
                    <td className="px-5 py-3 text-[hsl(var(--text-muted))]">{new Date(i.issued_date).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-right font-mono font-semibold">{gbp(i.amount)}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity">
                        {i.hosted_invoice_url && (
                          <a href={i.hosted_invoice_url} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost"><Download className="w-3.5 h-3.5" strokeWidth={1.6}/></Button></a>
                        )}
                        <button onClick={() => detach(i.id)} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] hover:text-rose-600" title="Detach from project"><Unlink className="w-3.5 h-3.5" strokeWidth={1.6}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title={`Team (${assignees.length})`}>
          <div className="p-5 space-y-3">
            {assignees.length === 0 ? (
              <div className="text-[12px] text-[hsl(var(--text-muted))]">No team assigned. Edit the project to add members.</div>
            ) : assignees.map((m) => (
              <div key={m.id} className="flex items-center gap-3">
                <img src={m.avatar_url || "/brand/soubor-one-mark.png"} alt="" className="w-9 h-9 rounded-full object-cover"/>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold truncate">{m.name}</div>
                  <div className="text-[11px] text-[hsl(var(--text-muted))] truncate">{m.role_title}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded ${m.type === "external" ? "bg-amber-500/15 text-amber-600 dark:text-amber-400" : "bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))]"}`}>
                  {m.type === "external" ? "Ext" : "Int"}
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {tickets.length > 0 && (
        <Card
          title={`Linked tickets (${tickets.length})`}
          right={<Button size="sm" variant="ghost" onClick={() => nav("/tickets")} data-testid="open-tickets-page"><Ticket className="w-3.5 h-3.5" strokeWidth={1.6}/>Tickets</Button>}
          data-testid="project-linked-tickets"
        >
          <div className="divide-y divide-[hsl(var(--border))]">
            {tickets.map((t) => (
              <button
                key={t.id}
                onClick={() => nav("/tickets")}
                className="w-full text-left px-5 py-3 hover:bg-[hsl(var(--surface-hover))] flex items-center gap-3"
                data-testid="project-ticket-row"
              >
                <Ticket className={`w-4 h-4 shrink-0 ${t.internal ? "text-amber-600 dark:text-amber-400" : "text-[hsl(var(--primary))]"}`} strokeWidth={1.8}/>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold truncate">{t.subject}</div>
                  <div className="text-[11px] text-[hsl(var(--text-muted))]">
                    {t.number}
                    {t.internal && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400 text-[9px] uppercase tracking-widest font-bold">Internal</span>}
                    <span className="mx-1.5">·</span>
                    {t.priority}
                    <span className="mx-1.5">·</span>
                    Updated {new Date(t.updated_at).toLocaleDateString()}
                  </div>
                </div>
                <StatusPill variant={ticketStatusVariant(t.status)}>{t.status.replace("_", " ")}</StatusPill>
              </button>
            ))}
          </div>
        </Card>
      )}

      {work_orders.length > 0 && (
        <Card title={`Work orders (${work_orders.length})`}>
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))]">
                <th className="text-left font-semibold px-5 py-3">Title</th>
                <th className="text-left font-semibold px-5 py-3">Status</th>
                <th className="text-left font-semibold px-5 py-3">Priority</th>
                <th className="text-left font-semibold px-5 py-3">Assignee</th>
              </tr>
            </thead>
            <tbody>
              {work_orders.map((w) => (
                <tr key={w.id} className="border-t border-[hsl(var(--border))]">
                  <td className="px-5 py-3 font-medium">{w.title}</td>
                  <td className="px-5 py-3"><StatusPill variant={w.status === "completed" ? "success" : "default"}>{w.status}</StatusPill></td>
                  <td className="px-5 py-3">{w.priority}</td>
                  <td className="px-5 py-3">{w.assignee || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {showAttach && (
        <AttachInvoiceModal
          projectId={id}
          customerId={p.customer_id}
          onClose={() => setShowAttach(false)}
          onDone={() => { setShowAttach(false); load(); }}
        />
      )}
    </div>
  );
}

function AttachInvoiceModal({ projectId, customerId, onClose, onDone }) {
  const [invoices, setInvoices] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/invoices").then(({ data }) => {
      // Only show invoices not already attached, and prefer same customer first
      const free = (data || []).filter((i) => !i.project_id);
      free.sort((a, b) => {
        if (a.customer_id === customerId && b.customer_id !== customerId) return -1;
        if (b.customer_id === customerId && a.customer_id !== customerId) return 1;
        return 0;
      });
      setInvoices(free);
    });
  }, [customerId]);

  const attach = async (iid) => {
    await api.post(`/projects/${projectId}/attach-invoice/${iid}`);
    toast.success("Invoice attached");
    onDone();
  };

  const filtered = invoices.filter((i) => {
    if (!q) return true;
    return `${i.number} ${i.customer_name}`.toLowerCase().includes(q.toLowerCase());
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="attach-invoice-modal">
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Attach invoice to project</h3>
            <p className="text-[12px] text-[hsl(var(--text-muted))] mt-0.5">Same-customer invoices appear first. Only unallocated shown.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-4 border-b border-[hsl(var(--border))]">
          <Input placeholder="Search by number or customer…" value={q} onChange={(e) => setQ(e.target.value)}/>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-[13px] text-[hsl(var(--text-muted))]">No unallocated invoices match.</div>
          ) : filtered.map((i) => (
            <button
              key={i.id}
              onClick={() => attach(i.id)}
              className="w-full px-5 py-3 flex items-center gap-3 border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-hover))] text-left"
              data-testid="attach-invoice-option"
            >
              <div className="flex-1 min-w-0">
                <div className="font-mono font-semibold text-[13px]">{i.number}</div>
                <div className="text-[11px] text-[hsl(var(--text-muted))] truncate">{i.customer_name}</div>
              </div>
              <StatusPill variant={invoiceStatusVariant(i.status)}>{i.status}</StatusPill>
              <div className="font-mono font-semibold text-[13px]">{gbp(i.amount)}</div>
              <Link2 className="w-4 h-4 text-[hsl(var(--primary))]" strokeWidth={2}/>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
